// Color management utilities

import { COLOR_PALETTE } from '../constants';

/**
 * Color manager for type-based marker coloring
 */
export class ColorManager {
  private typeColorMap: Record<string, string>;
  private onColorChange?: (typeColorMap: Record<string, string>) => void;

  constructor(
    initialColorMap: Record<string, string> = {},
    onColorChange?: (typeColorMap: Record<string, string>) => void
  ) {
    this.typeColorMap = { ...initialColorMap };
    this.onColorChange = onColorChange;
  }

  /**
   * Get color for a type, assigning one if not exists
   * @param type - Type name
   * @returns Color hex code
   */
  getColor(type: string): string {
    if (this.typeColorMap[type]) {
      return this.typeColorMap[type];
    }

    // Assign next available color
    const usedColors = new Set(Object.values(this.typeColorMap));
    const availableColor = COLOR_PALETTE.find((c) => !usedColors.has(c)) || COLOR_PALETTE[0];

    this.typeColorMap[type] = availableColor;
    this.notifyChange();

    return availableColor;
  }

  /**
   * Set color for a type
   * @param type - Type name
   * @param color - Color hex code
   */
  setColor(type: string, color: string): void {
    this.typeColorMap[type] = color;
    this.notifyChange();
  }

  /**
   * Get all type-color mappings
   * @returns Type color map
   */
  getColorMap(): Record<string, string> {
    return { ...this.typeColorMap };
  }

  /**
   * Set entire color map
   * @param colorMap - New color map
   */
  setColorMap(colorMap: Record<string, string>): void {
    this.typeColorMap = { ...colorMap };
  }

  /**
   * Check if a type has an assigned color
   * @param type - Type name
   * @returns True if color exists
   */
  hasColor(type: string): boolean {
    return type in this.typeColorMap;
  }

  private notifyChange(): void {
    this.onColorChange?.(this.getColorMap());
  }
}

/**
 * Generate SVG marker icon with specified color
 * @param color - Marker fill color
 * @returns SVG string
 */
export function generateMarkerSvg(color: string): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41" width="25" height="41">
      <path fill="${color}" stroke="var(--marker-stroke, #fff)" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.3 12.5 28.5 12.5 28.5S25 20.8 25 12.5C25 5.6 19.4 0 12.5 0z"/>
      <circle cx="12.5" cy="12.5" r="6" fill="var(--marker-inner, #fff)" opacity="0.9"/>
    </svg>
  `;
}

/**
 * Convert SVG to data URI for use as icon
 * @param svg - SVG string
 * @returns Data URI string
 */
export function svgToDataUri(svg: string): string {
  return 'data:image/svg+xml;base64,' + btoa(svg);
}

/**
 * Generate a colored marker icon URL
 * @param color - Marker fill color
 * @returns Data URI string
 */
export function generateColoredIconUrl(color: string): string {
  return svgToDataUri(generateMarkerSvg(color));
}
